import { useNavigate } from "react-router";
import { toast } from "sonner";

import { useCan } from "@/app/session/permissions";
import { useSessionUser } from "@/app/session/useSession";
import { projectTarget, taskTarget, type LinkTarget } from "@/features/collaboration/lib/links";
import { useMarkAllNotificationsRead, useMarkNotificationRead } from "@/features/notifications/api";
import { peopleLink } from "@/features/notifications/lib/peopleSentences";
import { notificationDestination, type Notification } from "@/features/notifications/lib/sentences";
import { useT } from "@/shared/i18n/useT";

/** A task opens over its project (or My Work, outside any project); else the project itself. */
function destinationOf(projectKey: string | null, reference: string | null): LinkTarget | null {
  if (reference) return taskTarget(projectKey, reference);
  return projectKey === null ? null : projectTarget(projectKey);
}

/**
 * What the inbox does: opening a notification marks it read and goes to its task (or project);
 * "Mark all as read" clears the rest. Lives with the bell, which stays mounted after the panel
 * closes, so a failure still reaches the person as a toast.
 */
export function useNotificationActions(close: () => void) {
  const t = useT();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const canViewPeople = useCan("hr.view");
  const userId = useSessionUser()?.id ?? null;
  const reportFailure = () => toast.error(t("notifications.markError"));

  const open = (notification: Notification) => {
    if (notification.read_at === null) markRead.mutate(notification, { onError: reportFailure });
    close();
    const page = peopleLink(notification, { canViewPeople, userId });
    if (page) {
      void navigate(page);
      return;
    }
    const { projectKey, reference, ticketId } = notificationDestination(notification);
    if (ticketId) {
      void navigate(`/tickets/${ticketId}`);
      return;
    }
    const target = destinationOf(projectKey, reference);
    if (target === null) return;
    void navigate(target.to, { state: target.state });
  };

  const readAll = () => {
    markAll.mutate(undefined, { onError: reportFailure });
  };

  return { open, readAll, markingAll: markAll.isPending };
}
